import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI, extractErrorMessage } from '../../services/api';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import type { SignupFormData, PasswordStrength, EmailStatus } from './signupTypes';

export function checkPasswordStrength(password: string): PasswordStrength {
  const hasMinLength = password.length >= 8;
  const hasNumber = /[0-9]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const strength = [hasMinLength, hasNumber, hasUpperCase, hasLowerCase, hasSpecialChar].filter(Boolean).length;
  return {
    hasMinLength,
    hasNumber,
    hasUpperCase,
    hasLowerCase,
    hasSpecialChar,
    strength,
    isValid: strength >= 4
  };
}

const initialStrength: PasswordStrength = {
  hasMinLength: false,
  hasNumber: false,
  hasUpperCase: false,
  hasLowerCase: false,
  hasSpecialChar: false,
  strength: 0,
  isValid: false
};

export function useSignupPage() {
  const navigate = useNavigate();
  const { signup, login, user, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus>({ checking: false });
  const [isTouched, setIsTouched] = useState({ email: false, password: false });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(initialStrength);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const checkEmailAvailability = async (email: string) => {
    if (!email) return false;
    setEmailStatus({ checking: true });
    try {
      const response = await authAPI.checkEmail(email);
      setEmailStatus({ checking: false, available: response.available });
      return response.available;
    } catch (err) {
      console.error('Error checking email:', err);
      setEmailStatus({ checking: false });
      return false;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email && isTouched.email) {
        checkEmailAvailability(formData.email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email, isTouched.email]);

  useEffect(() => {
    if (formData.password) {
      setPasswordStrength(checkPasswordStrength(formData.password));
    } else {
      setPasswordStrength(initialStrength);
    }
  }, [formData.password]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'email' || name === 'password') {
      setIsTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    track(AnalyticsEvents.SIGNUP_START);
    setError('');
    setIsTouched({ email: true, password: true });
    const isEmailAvailable = await checkEmailAvailability(formData.email);
    if (!isEmailAvailable) {
      setError('This email is already registered');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!passwordStrength.isValid) {
      setError('Please ensure your password meets all the requirements');
      return;
    }
    if (!formData.phone || !formData.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    setLoading(true);
    try {
      await signup(formData.email, formData.password, formData.firstName, formData.lastName, formData.phone.trim());
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(extractErrorMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    error,
    loading,
    emailStatus,
    isTouched,
    setIsTouched,
    passwordStrength,
    handleChange,
    handleSubmit,
    checkEmailAvailability
  };
}
